import { useEffect, useMemo, useState } from "react";
import { api, type AdminUser } from "../lib/api.ts";

interface Props {
  syncKey: string;
}

export default function Admin({ syncKey }: Props) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [issuedToken, setIssuedToken] = useState("");
  const [days, setDays] = useState(365);

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0)),
    [users]
  );

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.users(syncKey);
      setUsers(res.users);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function issueKey() {
    setError(null);
    try {
      const res = await api.admin.issueKey(syncKey, {
        email: newEmail || null,
        isAdmin: newIsAdmin,
        days,
      });
      setIssuedToken(res.token);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to issue key");
    }
  }

  async function toggleAdmin(user: AdminUser) {
    setError(null);
    try {
      await api.admin.setAdmin(syncKey, user.id, !user.isAdmin);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update admin role");
    }
  }

  return (
    <div className="page">
      <h1>Admin</h1>
      <p>Issue sync keys and manage admin access.</p>

      {error && <p className="error-text">{error}</p>}

      <section className="settings-section">
        <h2>Issue key</h2>
        <div className="key-change-row" style={{ gap: 8, flexWrap: "wrap" }}>
          <input
            type="email"
            placeholder="User email (optional)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <input
            type="number"
            min={1}
            max={3650}
            value={days}
            onChange={(e) => setDays(Number(e.target.value) || 365)}
            style={{ width: 120 }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={newIsAdmin}
              onChange={(e) => setNewIsAdmin(e.target.checked)}
            />
            Admin
          </label>
          <button type="button" onClick={issueKey}>Issue key</button>
        </div>
        {issuedToken && (
          <div style={{ marginTop: 12 }}>
            <p className="success-text">Key issued. Copy and send to user:</p>
            <textarea
              readOnly
              value={issuedToken}
              style={{ width: "100%", minHeight: 120, fontFamily: "monospace" }}
            />
          </div>
        )}
      </section>

      <section className="settings-section">
        <h2>Users</h2>
        {loading ? (
          <p>Loading…</p>
        ) : sortedUsers.length === 0 ? (
          <p className="empty">No users yet.</p>
        ) : (
          <table className="settings-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Admin</th>
                <th>Last Seen</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div>{u.email || "(no email)"}</div>
                    <code>{u.id}</code>
                  </td>
                  <td>{u.isAdmin ? "yes" : "no"}</td>
                  <td>{u.lastSeenAt ? new Date(u.lastSeenAt * 1000).toLocaleString() : "never"}</td>
                  <td>
                    <button type="button" onClick={() => toggleAdmin(u)}>
                      {u.isAdmin ? "Revoke admin" : "Make admin"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
